import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
} from 'react'
import { useAxios } from '@/contexts/AxiosProvider'
import { useHall } from '@/contexts/HallProvider'
import { useUser } from '@clerk/clerk-react'
import { TurnStatus, TurnType } from '@db8/shared'
import { useMeetingAppContext } from '@/MeetingAppContextDef'

interface AzureTtsContextProps {
  playerReady: boolean
  isPlaying: boolean
  generateAudio: (text: string, botId?: number) => Promise<void>
  playReading: (position?: number) => void
  pauseReading: (position?: number) => void
  restartReading: (position?: number) => void
  cleanup: () => void
  muteAudio: () => void
  unmuteAudio: () => void
  fetchAudio: (fileName: string) => Promise<void>
  botArgument: string
  setBotArgument: Dispatch<SetStateAction<string>>
  cancelTts: () => void
  audioRef: any
}

const AzureTtsContext = createContext<AzureTtsContextProps | undefined>(undefined)

export const AzureTtsProvider = ({ children }: { children: ReactNode }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const objectUrlRef = useRef<string | null>(null)
  const lastFileRef = useRef<string | null>(null)
  const pendingPlayRef = useRef(false)
  const lastPlayedFileRef = useRef<string | null>(null)

  const [playerReady, setPlayerReady] = useState(false)
  const [botArgument, setBotArgument] = useState<string>('')

  const axios = useAxios()
  const { hall, setHallMetadata, setLocalMetadata, hallMetadata } = useHall()
  const user = useUser()
  const controllerRef = useRef<AbortController | null>(null)
  const ttsRate = (hallMetadata as any)?.ttsRate ?? 1
  const { selectedSpeaker } = useMeetingAppContext()

  // === Watch activeTts and load/unload audio defensively
  useEffect(() => {
    const url = hallMetadata?.activeTts
    if (url) {
      const lastPart = url.split('/').pop() || url
      const fileName = lastPart.split('?')[0]
      if (lastFileRef.current === fileName) return
      void fetchAudio(fileName)
    } else {
      // Only cleanup if a file had been loaded; prevents spurious teardown
      if (lastFileRef.current) {
        cleanup()
        lastFileRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallMetadata?.activeTts])
  // Apply rate & preserve pitch on any *existing* element when ttsRate changes
  useEffect(() => {
    const el = audioRef.current as HTMLAudioElement | null
    if (!el) return
    try {
      // keep pitch natural while speeding up
      ;(el as any).preservesPitch = true
      ;(el as any).mozPreservesPitch = true
      ;(el as any).webkitPreservesPitch = true

      el.playbackRate = Number(ttsRate) || 1
    } catch {}
  }, [hallMetadata?.ttsRate])
  // === Manual play/pause only - no automatic behavior

  const generateAudio = async (text: string, botId?: number) => {
    try {
      controllerRef.current = new AbortController()
      const hallId = hall.id
      const { data } = await axios.post(
        '/tts/generate-tts',
        { text, hallId, userId: user.user.id, botId },
        { signal: controllerRef.current.signal },
      )
      if (data?.activeTts) setHallMetadata({ activeTts: data.activeTts })
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // no-op
      } else {
        // no-op
      }
    }
  }

  const cancelTts = () => {
    controllerRef.current?.abort()
  }

  const playReading = (position?: number) => {
    if (!audioRef.current) {
      // If user pressed Play before the audio finished loading, latch the intent and autoplay once ready.
      pendingPlayRef.current = true
      return
    }
    pendingPlayRef.current = false

    // If this is a NEW audio file and caller didn't specify a position,
    // start at 0 to avoid carrying over a prior turn's saved ttsTime.
    const isNewFile = lastPlayedFileRef.current !== lastFileRef.current
    const currentTime = position ?? (isNewFile ? 0 : hallMetadata.ttsTime ?? 0)
    audioRef.current.currentTime = currentTime
    audioRef.current.play().catch(() => {})
    lastPlayedFileRef.current = lastFileRef.current
  }

  const pauseReading = () => {
    if (!audioRef.current) return
    const currentTime = audioRef.current.currentTime || 0
    audioRef.current.pause()
    setHallMetadata({ ttsTime: currentTime })
  }

  const restartReading = () => {
    if (audioRef.current) audioRef.current.currentTime = 0
  }

  const cleanup = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current = null
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    lastPlayedFileRef.current = null
    setPlayerReady(false)
  }

  useEffect(() => {
    const handler = () => {
      if (audioRef.current) {
        const t = audioRef.current.currentTime || 0
        setLocalMetadata((prev: any) => ({ ...prev, ttsTime: t }))
      }
      cleanup()
    }
    window.addEventListener('beforeunload', handler)
    return () => {
      window.removeEventListener('beforeunload', handler)
      if (audioRef.current) {
        const t = audioRef.current.currentTime || 0
        setLocalMetadata((prev: any) => ({ ...prev, ttsTime: t }))
      }
      cleanup()
    }
  }, [setLocalMetadata])

  const muteAudio = () => {
    if (audioRef.current) audioRef.current.muted = true
  }
  const unmuteAudio = () => {
    if (audioRef.current) audioRef.current.muted = false
  }

  const fetchAudio = async (fileName: string) => {
    if (lastFileRef.current === fileName) return
    lastFileRef.current = fileName
    try {
      // clear any existing element
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
        audioRef.current = null
      }

      const res = await axios.get(`/tts/fetchAudio/${fileName}`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data)
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = url

      const el = new Audio(url)
      el.preload = 'auto'

      // new lines to preserve pitch + apply hall-wide rate
      ;(el as any).preservesPitch = true
      ;(el as any).mozPreservesPitch = true
      ;(el as any).webkitPreservesPitch = true
      el.playbackRate = Number((hallMetadata as any)?.ttsRate) || 1

      // Route to selected output device when supported
      try {
        if (selectedSpeaker?.id && (el as any).setSinkId) {
          await (el as any).setSinkId(selectedSpeaker.id)
        }
      } catch (sinkErr) {
        // no-op
      }

      audioRef.current = el

      setHallMetadata({ ttsTime: 0 }) // new file, reset seek
      setPlayerReady(true)

      // If the user already hit Play, start immediately now that we have a valid element.
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false
        // Always start new audio from the beginning.
        try {
          el.currentTime = 0
        } catch {}
        el.play().catch(() => {})
        lastPlayedFileRef.current = lastFileRef.current
      }
    } catch (error) {
      setPlayerReady(false)
    }
  }

  const isPlaying = useMemo(() => hallMetadata.status === TurnStatus.PLAYING, [hallMetadata.status])

  return (
    <AzureTtsContext.Provider
      value={{
        playerReady,
        isPlaying,
        generateAudio,
        playReading,
        pauseReading,
        restartReading,
        cleanup,
        muteAudio,
        unmuteAudio,
        fetchAudio,
        botArgument,
        setBotArgument,
        cancelTts,
        audioRef,
      }}
    >
      {children}
    </AzureTtsContext.Provider>
  )
}

export const useAzureTts = () => {
  const context = useContext(AzureTtsContext)
  if (!context) throw new Error('useAzureTts must be used within an AzureTtsProvider')
  return context
}
